import React from "react";
//#region redux
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
//#endregion
//#region types
import { AxiosResponse } from "axios";
import { WintSuperkollAPIModel as ApiModels, WintUtilsWebApi } from "wint-api-client/dist/types";
import { TranslationKeys } from "translations";
import { Amount } from "models/utilTypes/currencyType";
import { CurrencyEnums, StyleEnums } from "models/enums";
import { CashReconciliationFormTypes, RevenueReportingFormTypes } from "models/formTypes";
import { Customer } from "models/propTypes/userProfilePropTypes";
import { AppState } from "store";
//#endregion
//#region components
import { StaticInfoMessage, TableAsync, Tags, useFilterContext } from "components/common";
import { useCashReconciliationService, useRevenueReportingService } from "serviceHooks";
//#endregion
import { LocalizationHelperInstance, TableHelper, UrlHelper } from "utils/helpers";
import { CommonConstants } from "utils/constants";
import { camelize } from "humps";
import cssExports from "theme/styles/main.module.scss";

interface RevenueReportListProps {
  reloadKey: number;
}

interface MergedReportRowBase {
  date: Date;
  ownerId?: number;
  ownerName?: string;
  templateName: string;
  costCenter: string[];
  project: string[];
  difference: number;
  amount: number;
}

interface MergedRevenueRow extends MergedReportRowBase {
  kind: "revenue";
  id?: string;
  status: ApiModels.RevenueReportStatus;
}

interface MergedLegacyRow extends MergedReportRowBase {
  kind: "legacy";
  id: number;
  status?: undefined;
}

type MergedReportRow = MergedRevenueRow | MergedLegacyRow;

interface MergedTruncation {
  shown: number;
  total: number;
}

const LegacyFetchCap = 500;
const BulkOrderByProperty = "Date";

const toRevenueRow = (r: ApiModels.RevenueReportDescriptor): MergedRevenueRow => ({
  kind: "revenue",
  id: r.id,
  date: r.date,
  ownerId: r.ownerId,
  ownerName: r.ownerName,
  templateName: r.templateName ?? "",
  costCenter: r.costCenter ? [r.costCenter] : [],
  project: r.project ? [r.project] : [],
  difference: r.difference,
  amount: r.amount,
  status: r.status,
});

const toCashReconciliationFilter = (
  formFilter?: RevenueReportingFormTypes.Filter
): CashReconciliationFormTypes.Filter => ({
  date: formFilter?.date,
  registeredBy: formFilter?.registeredBy,
  registeredById: formFilter?.registeredById,
  saleAmount: formFilter?.saleAmount,
  differenceAmount: formFilter?.differenceAmount,
});

const RevenueReportList: React.FC<RevenueReportListProps> = (props) => {
  const revenueReportingService = useRevenueReportingService();
  const cashReconciliationService = useCashReconciliationService();
  const location = useLocation();
  const navigate = useNavigate();

  type BulkResponses = [
    Awaited<ReturnType<typeof revenueReportingService.reportFilter>>,
    Awaited<ReturnType<typeof cashReconciliationService.reportFilter>>,
  ];

  const { filter } = useFilterContext<RevenueReportingFormTypes.Filter>();
  const dimensions = useSelector((store: AppState) => store.common.dimensions.cashReconciliationDimensions);

  const [truncation, setTruncation] = React.useState<MergedTruncation | undefined>(undefined);

  const bulkCacheRef = React.useRef<{ key: string; request: Promise<BulkResponses> } | undefined>(undefined);
  const reloadKeyRef = React.useRef(props.reloadKey);
  reloadKeyRef.current = props.reloadKey;

  const getDimensions = React.useCallback(
    (dimensionIds: string[], type: ApiModels.DimensionTypes) => {
      const dimension = dimensions?.find((item) => item.key.compatibilityType === type)?.value;
      return dimension?.filter((c) => c.id !== undefined && dimensionIds.includes(c.id)).map((d) => d.name ?? "") ?? [];
    },
    [dimensions]
  );

  const toLegacyRow = React.useCallback(
    (r: ApiModels.CashReconciliationInstanceDescriptor): MergedLegacyRow => ({
      kind: "legacy",
      id: r.id,
      date: r.date,
      ownerId: r.ownerId,
      ownerName: r.ownerName,
      templateName: "",
      costCenter: r.dimensions ? getDimensions(r.dimensions, ApiModels.DimensionTypes.CostCenter) : [],
      project: r.dimensions ? getDimensions(r.dimensions, ApiModels.DimensionTypes.Project) : [],
      difference: r.difference,
      amount: r.amount,
    }),
    [getDimensions]
  );

  const filterFunc = React.useMemo(
    () => async (baseFilter: WintUtilsWebApi.Filter) => {
      const showLegacy = filter?.hideLegacy === false && !filter?.templateId && filter?.status === undefined;

      if (!showLegacy) {
        setTruncation(undefined);
        const response = await revenueReportingService.reportFilter(baseFilter, filter);
        return {
          ...response,
          data: { ...response.data, items: (response.data?.items ?? []).map(toRevenueRow) },
        } as AxiosResponse<WintUtilsWebApi.ListResponse<MergedReportRow>>;
      }

      const perPage = baseFilter?.numPerPage ?? CommonConstants.DefaultTableRowCount;
      const currentPage = baseFilter?.page ?? 0;
      const bulkFilter = {
        ...baseFilter,
        page: 0,
        numPerPage: LegacyFetchCap,
        orderByProperty: BulkOrderByProperty,
        orderByDescending: true,
        thenByProperty: undefined,
      };

      const cacheKey = JSON.stringify({ bulkFilter, filter, reloadKey: reloadKeyRef.current });
      const requestBulk = (): Promise<BulkResponses> =>
        Promise.all([
          revenueReportingService.reportFilter(bulkFilter, filter),
          cashReconciliationService.reportFilter(bulkFilter, toCashReconciliationFilter(filter)),
        ]).catch((error) => {
          if (bulkCacheRef.current?.key === cacheKey) {
            bulkCacheRef.current = undefined;
          }
          throw error;
        });

      const previous = bulkCacheRef.current;
      const cached = previous && previous.key === cacheKey ? previous : { key: cacheKey, request: requestBulk() };
      bulkCacheRef.current = cached;

      const [revenue, legacy] = await cached.request;

      const rows: MergedReportRow[] = [
        ...(revenue.data?.items ?? []).map(toRevenueRow),
        ...(legacy.data?.items ?? []).map(toLegacyRow),
      ];

      const orderBy = camelize(baseFilter?.orderByProperty ?? "date");
      const sorted = TableHelper.tableSortAsync(
        rows,
        TableHelper.getComparator(baseFilter?.orderByDescending ? "desc" : "asc", orderBy)
      );
      const start = currentPage * perPage;

      const trueTotal = (revenue.data?.totalItems ?? 0) + (legacy.data?.totalItems ?? 0);
      setTruncation(trueTotal > sorted.length ? { shown: sorted.length, total: trueTotal } : undefined);

      return {
        ...revenue,
        data: {
          items: sorted.slice(start, start + perPage),
          page: currentPage,
          numPerPage: perPage,
          totalItems: sorted.length,
          totalItemsWithOutFilter: sorted.length,
        },
      } as AxiosResponse<WintUtilsWebApi.ListResponse<MergedReportRow>>;
    },
    [filter, toLegacyRow]
  );

  const getRowPath = (row: MergedReportRow): string => {
    switch (row.kind) {
      case "legacy":
        return `/CashReconciliation/${row.id}`;
      case "revenue":
        return `/RevenueReport/${row.id}`;
    }
  };

  const onRowClick = (row: MergedReportRow) => {
    if (!row.id) {
      return;
    }
    navigate(UrlHelper.formatPath(getRowPath(row)), {
      state: { background: location, preserveSearch: true },
    });
  };

  const formatStatus = (row: MergedReportRow): string => {
    switch (row.kind) {
      case "legacy":
        return "";
      case "revenue":
        return LocalizationHelperInstance.getFormattedMessage(
          row.status === ApiModels.RevenueReportStatus.Voided
            ? TranslationKeys.RevenueReporting.StatusVoided
            : TranslationKeys.RevenueReporting.StatusReported
        );
    }
  };

  return (
    <>
      {truncation && (
        <StaticInfoMessage displayType={StyleEnums.DisplayType.Info} gutterBottom>
          {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.RevenueReporting.ListTruncated, undefined, {
            shown: truncation.shown,
            total: truncation.total,
          })}
        </StaticInfoMessage>
      )}
      <TableAsync<MergedReportRow>
        properties={{
          date: {
            description: TranslationKeys.Common.Date,
            formatter: (r: MergedReportRow) => new Date(r.date),
            noWrap: true,
            maxWidth: 130,
          },
          ownerName: {
            description: TranslationKeys.CashReconciliation.RegisteredBy,
            formatter: (r: MergedReportRow) => new Customer(r.ownerId, r.ownerName),
            noWrap: true,
            maxWidth: 220,
          },
          templateName: {
            description: TranslationKeys.RevenueReporting.PaymentMethod,
            formatter: (r: MergedReportRow) => r.templateName,
            noWrap: true,
            maxWidth: 200,
          },
          costCenter: {
            description: TranslationKeys.Settings.CostCenter,
            formatter: (r: MergedReportRow) => <Tags tags={r.costCenter} maxDisplayCount={1} />,
            maxWidth: 120,
            noWrap: true,
          },
          project: {
            description: TranslationKeys.Settings.Project,
            formatter: (r: MergedReportRow) => <Tags tags={r.project} maxDisplayCount={1} />,
            maxWidth: 120,
            noWrap: true,
          },
          difference: {
            description: TranslationKeys.RevenueReporting.Difference,
            formatter: (r: MergedReportRow) => new Amount(CurrencyEnums.CurrencyCode.SEK, r.difference),
            width: 200,
            alignment: "right",
            noWrap: true,
            classification: "amount",
          },
          amount: {
            description: TranslationKeys.Common.Amount,
            formatter: (r: MergedReportRow) => new Amount(CurrencyEnums.CurrencyCode.SEK, r.amount),
            width: 200,
            alignment: "right",
            noWrap: true,
            className: cssExports.textBlack,
          },
          status: {
            description: TranslationKeys.Common.Status,
            formatter: (r: MergedReportRow) => formatStatus(r),
            noWrap: true,
            maxWidth: 140,
          },
        }}
        filter={filterFunc}
        pagination="advanced"
        orderByProperty="date"
        orderByDescending
        rowClickHandler={onRowClick}
        reloadKey={props.reloadKey}
        maxRowCount={10}
      />
    </>
  );
};
export default RevenueReportList;
